/**
 *! Country supported by ClickIPP and their corresponding locales
 * This mapping is based on the countries listed in the ClickIPP documentation and their associated
 * locales.
 * It serves as a reference for determining the appropriate locale to use based on the country.
 * The getLocaleFromCountry function utilizes this mapping to return the correct locale for a given
 * country.
 * If the country is not found in the mapping, it defaults to 'en-US'.
 *
 * EXTRA INFORMATION:
 *! en-001 — en-001 is the standard BCP 47 tag for "English, World" (UN M.49 numeric region code),
 *! accepted by Intl APIs.
 */
const COUNTRY_LOCALE_MAP = {
  uk: 'en-GB',
  'canada-english': 'en-CA',
  'canada-french': 'fr-CA',
  mexico: 'es-MX',
  japan: 'ja-JP',
  singapore: 'en-SG',
  hongkong: 'en-HK',
  'hongkong-chinese': 'ch-HK',
  australia: 'en-AU',
  germany: 'de-DE',
  italy: 'it-IT',
  austria: 'de-AT',
  france: 'fr-FR',
  netherlands: 'nl-NL',
  newzealand: 'en-NZ',
  spain: 'es-ES',
  us: 'en-US',
  india: 'en-IN',
  malaysia: 'en-MY',
  sweden: 'sv-SE',
  taiwan: 'zh-TW',
  'thailand-en': 'en-TH',
  'thailand-th': 'th-TH',
  argentina: 'es-AR',
  brazil: 'pt-BR',
  philippines: 'en-PH',
  apaidc: 'en-x-apaidc',
  'iec-en': 'en-001',
  'idc-sp': 'es-x-idcsp',
  belgium: 'fr-BE',
  'belgium-dutch': 'nl-BE'
};
export const COUNTRY_SIDE_NAV = ['australia', 'uk', 'canada-english', 'canada-french', 'mexico', 'italy', 'iec-en', 'apaidc'];

// for Skipping the preference page
export const SKIP_PREFERENCE_LOCALES = ['en-US', 'fr-BE', 'nl-BE'];

/**
 * Default fallback locale
 */
export const DEFAULT_LOCALE = 'en-US';

/**
 * Get locale from country string
 * @param {string} country
 * @returns {string} locale
 */
export const getLocaleFromCountry = country => {
  if (!country || typeof country !== 'string') {
    return DEFAULT_LOCALE;
  }
  const normalizedCountry = country.toLowerCase();
  return COUNTRY_LOCALE_MAP[normalizedCountry] || DEFAULT_LOCALE;
};
export const GLOBAL_PREF_CODE = 'MKTGBEML';

/**
 * Validate if country exists in the configured map.
 * @param {string} country
 * @returns {boolean}
 */
export const isSupportedCountry = country => {
  if (!country || typeof country !== 'string') {
    return false;
  }
  return Object.prototype.hasOwnProperty.call(COUNTRY_LOCALE_MAP, country.toLowerCase());
};

/**
 * Validate if locale file exists (optional safety helper)
 */
export const isSupportedLocale = locale => Object.values(COUNTRY_LOCALE_MAP).includes(locale);

/**
 * Extract country from privacy pathname (/privacy/:country)
 * @param {string} pathname
 * @returns {string}
 */
export const getCountryFromPrivacyPathname = (pathname = '') => {
  if (!pathname || typeof pathname !== 'string') {
    return '';
  }
  const [, modulePath, country] = pathname.toLowerCase().split('/');
  if (modulePath !== 'privacy') {
    return '';
  }
  return country || '';
};

/**
 * Check if the current pathname is a privacy market that supports side nav
 * @param {string} pathname
 * @returns {boolean}
 */
export const isSideNavMarketPathname = (pathname = '') => COUNTRY_SIDE_NAV.includes(getCountryFromPrivacyPathname(pathname));
export const validateEmailRegex = value => {
  if (typeof value !== 'string' || value.length === 0) {
    return false;
  }
  const atIndex = value.indexOf('@');
  const domain = value.slice(atIndex + 1);
  return atIndex > 0 && atIndex === value.lastIndexOf('@') && domain.length > 0 && domain.includes('.') && ![...value].some(character => character.trim() === '');
};
export const APP_MODULE_NAME = 'axp-digital-identity-unsubscribe';
export function isUkMarketPathname(pathname = '') {
  return getCountryFromPrivacyPathname(pathname) === 'uk';
}
export function shouldSkipPreferencePageByLocale(locale) {
  return SKIP_PREFERENCE_LOCALES.includes(locale);
}
export function isUsMarketPathname(activeLocale) {
  return activeLocale === 'en-us' || activeLocale === 'en_us';
}
export function isMexicoMarketPathname(activeLocale) {
  return activeLocale === 'es-mx' || activeLocale === 'es_mx';
}
export function isTaiwanMarketPathname(activeLocale) {
  return activeLocale === 'zh-tw' || activeLocale === 'zh_tw';
}
export function correlationIdGenerator() {
  return `DIGUNSUB_${Date.now()}`;
}